import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Book, Code, PenTool, Briefcase, Search } from 'lucide-react';
import { useOasisBackend } from '../../../hooks/useOasisBackend';
import { ReadDatabase, DatabaseList } from '../../../api/OasisBackendApi';

interface DomainSelectorProps {
  onDomainSelect: (domainId: string | { id: string; name: string }[]) => void;
  multiSelect?: boolean;
  initialDomains?: string[]
  domains: ReadDatabase[]
}

const DomainSelector: React.FC<DomainSelectorProps> = ({ onDomainSelect, multiSelect = false, initialDomains = [], domains }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  const [selectedDomains, setSelectedDomains] = useState<string[]>(initialDomains || []);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const updateDropdownPosition = () => {
      if (!buttonRef.current) return;
      
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const spaceBelow = windowHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const dropdownHeight = 250; // Approximate height of dropdown

      setDropdownPosition(spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove ? 'bottom' : 'top');
    };

    if (isOpen) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition);
      window.addEventListener('resize', updateDropdownPosition);
      // Focus search input when dropdown opens
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }

    return () => {
      window.removeEventListener('scroll', updateDropdownPosition);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [isOpen]);

  // Reset search query when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  // Filter domains based on search query
  const filteredDomains = domains.filter(domain => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      domain.name.toLowerCase().includes(query) ||
      (domain.description && domain.description.toLowerCase().includes(query))
    );
  });

  const handleDomainClick = (domainId: string) => {
    if (multiSelect) {
      const newSelectedDomains = selectedDomains.includes(domainId)
        ? selectedDomains.filter(id => id !== domainId)
        : [...selectedDomains, domainId];
      setSelectedDomains(newSelectedDomains);
      
      // For multi-select, pass the full domain objects with id and name
      const selectedDomainObjects = newSelectedDomains.map(id => {
        const domain = domains.find(d => d.id === id);
        return { id, name: domain?.name || id };
      });
      onDomainSelect(selectedDomainObjects);
    } else {
      setSelectedDomains([domainId]);
      onDomainSelect(domainId);
      setIsOpen(false);
    }
  };

  const displayText = multiSelect
    ? selectedDomains.length > 0
      ? `${selectedDomains.length} domains enabled`
      : 'Select domains'
    : selectedDomains.length > 0
      ? (() => {
          const selectedDomain = domains.find(d => d.id === selectedDomains[0]);
          const domainName = selectedDomain?.name || selectedDomains[0];
          return domainName.length > 15 ? `${domainName.substring(0, 15)}...` : domainName;
        })()
      : 'Select domain';

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-1.5 text-sm rounded-md border border-[var(--rhea-card-border)] bg-[var(--rhea-bg-primary)] hover:bg-[var(--rhea-bg-tertiary)] transition-all duration-200"
      >
        <div className="flex items-center gap-2">
          <Book className="w-4 h-4 text-[var(--rhea-text-secondary)]" />
          <span className="text-[var(--rhea-text-primary)]">{displayText}</span>
        </div>
        <ChevronDown 
          className={`h-4 w-4 text-[var(--rhea-text-secondary)] transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div 
          className={`absolute ${
            dropdownPosition === 'bottom' ? 'top-full mt-1' : 'bottom-full mb-1'
          } left-0 right-0 bg-[var(--rhea-bg-primary)] border border-[var(--rhea-card-border)] rounded-md shadow-lg z-50 max-h-[250px] flex flex-col`}
        >
          {/* Search input */}
          <div className="p-2 border-b border-[var(--rhea-card-border)] sticky top-0 bg-[var(--rhea-bg-primary)]">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[var(--rhea-text-secondary)]" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                placeholder="Search domains..."
                className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md border border-[var(--rhea-card-border)] bg-[var(--rhea-bg-secondary)] text-[var(--rhea-text-primary)] placeholder-[var(--rhea-text-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--rhea-accent-primary)]"
              />
            </div>
          </div>

          {/* Domain list */}
          <div className="overflow-y-auto flex-1">
            {domains.length === 0 ? (
              <div className="px-3 py-2 text-sm text-[var(--rhea-text-secondary)]">
                No domains available
              </div>
            ) : filteredDomains.length === 0 ? (
              <div className="px-3 py-2 text-sm text-[var(--rhea-text-secondary)]">
                No domains match "{searchQuery}"
              </div>
            ) : (
              filteredDomains.map((domain) => (
                <button
                  key={domain.id}
                  onClick={() => handleDomainClick(domain.id)}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-[var(--rhea-bg-tertiary)] transition-colors flex items-center gap-2 ${
                    (multiSelect ? selectedDomains.includes(domain.id) : false)
                      ? 'text-[var(--rhea-accent-primary)]'
                      : 'text-[var(--rhea-text-primary)]'
                  }`}
                >
                  <Book className="w-4 h-4 text-[var(--rhea-text-secondary)]" />
                  <div className="flex flex-col">
                    <span>{domain.name}</span>
                    {domain.description && (
                      <span className="text-xs text-[var(--rhea-text-secondary)]">{domain.description}</span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DomainSelector; 